import type { SidebarMenuItem } from './types';

/**
 * 항목 자신이 현재 경로를 소유하는지 — 제외 규칙까지 반영한 단일 판정.
 * 행의 활성 표시·조상 표시·자동 펼침이 모두 이 판정을 공유해야 한 항목만 활성으로 남는다.
 */
export const isItemActive = (item: SidebarMenuItem, pathname: string): boolean => {
  if (item.excludeActivePaths?.some((excluded) => pathname.startsWith(excluded))) return false;
  if (item.path && pathname.startsWith(item.path)) return true;
  // lazy 부모의 prefix는 "자손이 활성"이라는 뜻이라 자기 활성에서 뺀다 — lazy 자식(수업 행)은 path 외의 prefix로도 활성
  return !item.lazyExpandChildren && matchesActivePathPrefix(item, pathname);
};

/** lazy 메뉴는 자식이 채워지기 전이므로 경로 prefix로 대신 판정한다 */
export const matchesActivePathPrefix = (item: SidebarMenuItem, pathname: string): boolean =>
  !!item.activePathPrefix?.some((prefix) => pathname.startsWith(prefix));

/** 자손 중 현재 경로를 소유한 항목이 있는지 (중첩 깊이 무관) */
export const hasActiveDescendant = (items: SidebarMenuItem[] | undefined, pathname: string): boolean =>
  !!items?.some((item) => isItemActive(item, pathname) || matchesActivePathPrefix(item, pathname) || hasActiveDescendant(item.children, pathname));

/**
 * 현재 경로를 소유한 항목에 도달하기까지 펼쳐야 하는 조상들의 키 목록.
 * 키 규칙은 SidebarNavItem의 expandKey와 같아야 한다 — 조상 id를 '/'로 이어붙인다.
 * 못 찾으면 null — "찾았지만 펼칠 조상이 없음"(빈 배열)과 구분해야 재귀가 조상을 쌓을지 판단할 수 있다.
 */
export function findAncestorExpandKeys(items: SidebarMenuItem[], pathname: string, parentKey?: string): string[] | null {
  for (const item of items) {
    const expandKey = parentKey ? `${parentKey}/${item.id}` : item.id;

    // lazy 메뉴는 펼쳐야 자식을 받아오므로 자기 자신도 펼침 대상에 포함한다
    if (item.lazyExpandChildren && matchesActivePathPrefix(item, pathname)) return [expandKey];
    // 활성 항목 자신은 펼칠 대상이 아니다 (자식이 있어도 강제로 열지 않는다)
    if (isItemActive(item, pathname)) return [];

    const descendantKeys = item.children?.length ? findAncestorExpandKeys(item.children, pathname, expandKey) : null;
    if (descendantKeys) return [expandKey, ...descendantKeys];
  }

  return null;
}
